import { Box, TextField } from "@mui/material";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
import type {
	ColDef,
	FirstDataRenderedEvent,
	GridApi,
	GridReadyEvent,
	RowClassParams,
	SelectionChangedEvent,
} from "ag-grid-community";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CustomLoadingOverlay from "@/components/loaders/LazyLoading/LazyLoading";

// The global default enables per-row auto-height, whose measurement depends on font/render
// timing — in the production build it mis-measures some rows and leaves "ghost" empty space
// (seen on staging, not local). Fixed row height keeps every row identical in both environments.
const PICKER_COL_DEF = {
	...globalDefaultColDef,
	autoHeight: false,
	wrapText: false,
};

// Selection-changed sources that come from our own programmatic pre-selection (deselectAll +
// setSelected), which must NOT be treated as a user change. Everything else is user-driven.
const PROGRAMMATIC_SELECTION_SOURCES = new Set([
	"api",
	"apiSelectAll",
	"apiSelectAllFiltered",
	"apiSelectAllCurrentPage",
	"rowDataChanged",
	"gridInitializing",
]);

interface Props {
	rows: any[];
	// field used to match a row against selectedIds (must be one of the columns)
	idField: string;
	columns: ColDef[];
	selectedIds: string[];
	onSelectionChanged: (rows: any[]) => void;
	isLoading: boolean;
	height?: number | string;
	searchPlaceholder?: string;
}

// Reusable picker table, mirroring the resource pickers (DocumentsTable / PartsTable):
// quick-filter search, AG-Grid's built-in selection column with header select-all
// (which keeps the header checkbox aligned with the row checkboxes), selected rows
// floated to the top, and a highlight on selected rows. Pre-selects rows whose idField
// is in selectedIds.
export const AssemblySelectionTable = ({
	rows,
	idField,
	columns,
	selectedIds,
	onSelectionChanged,
	isLoading,
	height = "48vh",
	searchPlaceholder = "Search",
}: Props) => {
	const [filterInput, setFilterInput] = useState("");
	const gridApiRef = useRef<GridApi | null>(null);
	const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

	const columnDefs = useMemo<ColDef[]>(
		() => columns.map((c) => ({ sortable: true, ...c })),
		[columns],
	);

	// Pin selected rows to the top, deterministically, after EVERY sort — more reliable than a
	// selection-aware comparator, which AG Grid caches and did not re-float on user clicks in the
	// large parts list (it worked on the small drawings list by luck of no cache hit).
	const postSortRows = useCallback((params: { nodes: any[] }) => {
		const nodes = params.nodes;
		const selected = nodes.filter((n) => n.isSelected?.());
		if (!selected.length || selected.length === nodes.length) return;
		const unselected = nodes.filter((n) => !n.isSelected?.());
		nodes.length = 0;
		nodes.push(...selected, ...unselected);
	}, []);

	const loadingOverlayComponent = useMemo<any>(() => CustomLoadingOverlay, []);

	const sortRows = useCallback(() => {
		gridApiRef.current?.applyColumnState({
			state: [{ colId: idField, sort: "asc", sortIndex: 0 }],
			defaultState: { sort: null },
		});
	}, [idField]);

	const getRowStyle = useCallback((params: RowClassParams) => {
		return params.node.isSelected() ? { background: "#e3f2fd" } : undefined;
	}, []);

	const applySelection = useCallback(() => {
		const api = gridApiRef.current;
		if (!api) return;
		api.deselectAll();
		api.forEachNode((node) => {
			if (node.data && selectedSet.has(String(node.data[idField]))) {
				node.setSelected(true);
			}
		});
		sortRows();
		api.refreshClientSideRowModel("sort");
		// Report the settled baseline selection to the parent once.
		setTimeout(() => {
			onSelectionChanged(gridApiRef.current?.getSelectedRows() ?? []);
		}, 0);
	}, [selectedSet, idField, sortRows, onSelectionChanged]);

	const onGridReady = useCallback((e: GridReadyEvent) => {
		gridApiRef.current = e.api;
	}, []);

	// Stable row identity keyed on idField. Callers dedupe rows by idField before passing them in,
	// so it is unique here — which is exactly why a plain getRowId is safe now (the earlier "ghost
	// rows" came from a getRowId colliding on duplicate names). A stable id keeps selection mapped
	// to the right row across the sort-to-top reorder instead of falling back to a shifting index.
	const getRowId = useCallback(
		(p: { data: any }) => String(p.data[idField]),
		[idField],
	);

	const onFirstDataRendered = useCallback(
		(_e: FirstDataRenderedEvent) => {
			applySelection();
		},
		[applySelection],
	);

	const onAgSelectionChanged = useCallback(
		(e: SelectionChangedEvent) => {
			// Only react to USER selection; skip the programmatic pre-selection. Using the event
			// source (deterministic) instead of a timing flag, which desynced on the large parts
			// list and dropped the sort-to-top on user clicks.
			if (PROGRAMMATIC_SELECTION_SOURCES.has(e.source)) return;
			onSelectionChanged(e.api.getSelectedRows());
			// Re-run the sort so postSortRows floats the just-selected rows to the top.
			sortRows();
			e.api.refreshClientSideRowModel("sort");
		},
		[onSelectionChanged, sortRows],
	);

	useEffect(() => {
		gridApiRef.current?.setGridOption("quickFilterText", filterInput.trim());
	}, [filterInput]);

	useEffect(() => {
		if (gridApiRef.current && !isLoading) {
			const t = setTimeout(() => applySelection(), 50);
			return () => clearTimeout(t);
		}
	}, [rows, isLoading, applySelection]);

	return (
		<>
			<TextField
				label={searchPlaceholder}
				variant="outlined"
				value={filterInput}
				onChange={(e) => setFilterInput(e.target.value)}
				margin="dense"
				size="small"
				fullWidth
			/>
			<Box sx={{ width: "100%", height }}>
				<AgGridReact
					rowHeight={30}
					headerHeight={28}
					// No row animation (matches the working PartsTable/DocumentsTable pickers) plus a
					// getRowId keyed on the deduped idField: unique ids stop AG Grid from mis-mapping
					// selection to a shifting row index, which is what produced the "ghost" rows.
					animateRows={false}
					getRowId={getRowId}
					suppressRowVirtualisation={false}
					suppressColumnVirtualisation={false}
					rowBuffer={10}
					debounceVerticalScrollbar={true}
					onGridReady={onGridReady}
					rowData={rows}
					columnDefs={columnDefs}
					defaultColDef={PICKER_COL_DEF}
					onFirstDataRendered={onFirstDataRendered}
					loadingOverlayComponent={loadingOverlayComponent}
					rowSelection={{
						mode: "multiRow",
						checkboxes: true,
						// Header select-all removed for now: it does not line up with the
						// row checkboxes in this table.
						headerCheckbox: false,
						enableClickSelection: true,
						hideDisabledCheckboxes: false,
						enableSelectionWithoutKeys: true,
					}}
					onSelectionChanged={onAgSelectionChanged}
					postSortRows={postSortRows}
					suppressContextMenu={true}
					loading={isLoading}
					getRowStyle={getRowStyle}
					quickFilterText={filterInput}
				/>
			</Box>
		</>
	);
};
